import { useMemo, useState } from "react";

import { Button, DateTimeInput, PlayerPicker, Sheet, SpotPicker } from "@/ui";
import {
  searchPlayers,
  useCreateChallenge,
  useMyChallenges,
  useSpots,
} from "@/features/queries";
import { useAuth } from "@/contexts/AuthContext";
import { useDailyChallengeLimit } from "@/features/challenges/useDailyChallengeLimit";

interface Props {
  open: boolean;
  onClose: () => void;
}

type Mode = "now" | "scheduled";

export function CreateChallengeDialog({ open, onClose }: Props) {
  return (
    <Sheet open={open} onClose={onClose} title="Вызвать игрока">
      {open && <CreateChallengeForm onClose={onClose} />}
    </Sheet>
  );
}

function CreateChallengeForm({ onClose }: { onClose: () => void }) {
  const { user } = useAuth();
  const { data: spots = [] } = useSpots();
  const { data: myChallenges = [] } = useMyChallenges();
  const create = useCreateChallenge();
  const dailyLimit = useDailyChallengeLimit();

  const [opponentId, setOpponentId] = useState<number | null>(null);
  const [mode, setMode] = useState<Mode>("now");
  const [dateTime, setDateTime] = useState<string | null>(() => {
    const d = new Date();
    d.setHours(d.getHours() + 1, 0, 0, 0);
    return d.toISOString();
  });
  const [spotId, setSpotId] = useState<number | null>(null);

  // Users with whom we already have a pending/accepted challenge — can't
  // open a second one until the first resolves, so hide them from the
  // picker instead of letting the server 400 on submit.
  const excludeIds = useMemo(() => {
    const ids = new Set<number>();
    if (user) ids.add(user.id);
    if (user) {
      for (const c of myChallenges) {
        if (c.status !== "pending" && c.status !== "accepted") continue;
        ids.add(c.challenger.id === user.id ? c.challenged.id : c.challenger.id);
      }
    }
    return Array.from(ids);
  }, [user, myChallenges]);

  const canSubmit =
    opponentId !== null &&
    (mode === "now" || dateTime !== null) &&
    !create.isPending &&
    !dailyLimit.atLimit;

  const submit = async () => {
    if (opponentId == null) return;
    try {
      await create.mutateAsync({
        challenged_user_id: opponentId,
        scheduled_at: mode === "now" ? null : dateTime,
        spot_id: mode === "scheduled" ? spotId : null,
      });
      onClose();
    } catch {
      /* error shown below */
    }
  };

  return (
    <div
      style={{
        padding: "0 16px 8px",
        display: "flex",
        flexDirection: "column",
        gap: 14,
      }}
    >
        <Field label="Кого вызываем">
          <PlayerPicker
            value={opponentId}
            onChange={setOpponentId}
            excludeIds={excludeIds}
            searchFn={searchPlayers}
          />
          {excludeIds.length > 1 && (
            <span
              className="caption"
              style={{ color: "var(--fg3)", marginTop: 4 }}
            >
              Игроки с активным вызовом скрыты — завершите или отмените
              предыдущий вызов, чтобы бросить новый.
            </span>
          )}
        </Field>

        <Field label="Когда">
          <div style={{ display: "flex", gap: 8 }}>
            <Button
              fullWidth
              variant={mode === "now" ? "primary" : "secondary"}
              size="sm"
              onClick={() => setMode("now")}
            >
              Здесь и сейчас
            </Button>
            <Button
              fullWidth
              variant={mode === "scheduled" ? "primary" : "secondary"}
              size="sm"
              onClick={() => setMode("scheduled")}
            >
              Время и место
            </Button>
          </div>
        </Field>

        {mode === "scheduled" && (
          <>
            <Field label="Дата и время">
              <DateTimeInput value={dateTime} onChange={setDateTime} required />
            </Field>

            <Field label="Место">
              <SpotPicker
                spots={spots}
                value={spotId}
                onChange={setSpotId}
                emptyLabel="По договорённости"
              />
            </Field>
          </>
        )}

        {dailyLimit.atLimit && (
          <div
            style={{
              background: "var(--bg-section)",
              color: "var(--fg2)",
              border: "1px solid var(--separator)",
              padding: "10px 12px",
              borderRadius: "var(--r-md)",
              fontSize: 13,
              textAlign: "center",
            }}
          >
            На сегодня лимит вызовов исчерпан ({dailyLimit.used} из{" "}
            {dailyLimit.limit}). Попробуйте завтра.
          </div>
        )}

        {create.error && (
          <div
            style={{
              background: "rgba(223,63,64,0.1)",
              color: "var(--fg-danger)",
              padding: "8px 12px",
              borderRadius: "var(--r-md)",
              fontSize: 13,
            }}
          >
            {(create.error as Error).message}
          </div>
        )}

        <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
          <Button fullWidth onClick={submit} disabled={!canSubmit}>
            {create.isPending ? "Отправляем…" : "Бросить вызов"}
          </Button>
          <Button fullWidth variant="ghost" onClick={onClose} disabled={create.isPending}>
            Отмена
          </Button>
        </div>
    </div>
  );
}

/**
 * Plain <div> wrapper (not <label>) — PlayerPicker/SpotPicker are composite
 * widgets, and a surrounding <label> tries to delegate clicks to the first
 * focusable child, which in Yandex/some Chromium variants swallows clicks
 * on the picker's inner buttons. See bug: второй юзер не может выбрать.
 */
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <span className="label" style={{ color: "var(--fg2)" }}>{label}</span>
      {children}
    </div>
  );
}
